import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@locaisolutions/icons/dist/icons24px/Close24Px";

import { getPendingConfigurationItemsList } from "../../redux/selectors/siteSelectors";
import PendingConfigurationItemValuesChange from "./PendingConfigurationItemValuesChange";
import {
  deletePendingChangesListEntry,
  SiteOperationTempDataType
} from "./siteSlice";
import { useAppDispatch } from "../../store";
import Loading from "../Loading";
import Stack from "@mui/material/Stack";
import { getSelectedDrawersData } from "../../redux/selectors/drawerSelectors";

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  backgroundColor: theme.palette.secondary.main,
  border: `1px solid ${theme.palette.divider}`,
  borderWidth: "1px 0px 1px 1px",
  gridColumn: "3/4",
  padding: "10px"
}));

const MainPendingChangesListContainer = styled(Stack)(() => ({
  flexDirection: "column",
  gap: "10px"
}));

const MainHeaderContainer = styled(Stack)(() => ({
  alignItems: "center",
  justifyContent: "space-between",
  flexDirection: "row"
}));
type PendingChangesListProps = {
  siteOperationTempData?: SiteOperationTempDataType;
};
function PendingChangesList(props: PendingChangesListProps) {
  const { siteOperationTempData } = props;
  const { isSiteOperationPending = false } = siteOperationTempData || {};
  const selectedSiteDrawerData = useSelector(getSelectedDrawersData).site;
  const pendingConfigurationChangesList = useSelector(
    getPendingConfigurationItemsList
  );
  const dispatch = useAppDispatch();

  return (
    <MainContainer>
      {isSiteOperationPending ? (
        <Loading numberOfRows={15} />
      ) : (
        Object.keys(pendingConfigurationChangesList).map(
          (pendingConfigurationItemId, i) => {
            const pendingChange =
              pendingConfigurationChangesList[pendingConfigurationItemId];

            const {
              sectionName,
              configurationName,
              configurationItemId,
              sectionId,
              currentConfigurationItemValues,
              originalConfigurationItemValues,
              dataType,
              isPartOfSiteDuplicationOrCreation,
              validationRules
            } = pendingChange;

            return (
              <MainPendingChangesListContainer
                key={`pending-change-${sectionId}-${configurationItemId}${i}`}
                data-testid="pending-change-item"
              >
                <MainPendingChangesListContainer>
                  <MainHeaderContainer>
                    <Typography
                      sx={{
                        fontSize: 20
                      }}
                      data-testid={`section-name`}
                    >
                      {sectionName}
                    </Typography>
                    {!isPartOfSiteDuplicationOrCreation && (
                      <IconButton
                        data-testid="remove-pending-change-item-btn"
                        aria-label="remove-pending-change-item-btn"
                        onClick={() => {
                          dispatch(
                            deletePendingChangesListEntry({
                              pendingConfigurationItemId,
                              siteId: selectedSiteDrawerData?.id || ""
                            })
                          );
                        }}
                        sx={{
                          ml: "auto"
                        }}
                      >
                        <CloseIcon
                          style={{
                            height: 16,
                            width: 16
                          }}
                        />
                      </IconButton>
                    )}
                  </MainHeaderContainer>
                  <Typography
                    sx={{
                      fontSize: 14
                    }}
                    data-testid={`config-item-name-${configurationItemId}`}
                  >
                    {configurationName}
                  </Typography>
                  {!isPartOfSiteDuplicationOrCreation ? (
                    <PendingConfigurationItemValuesChange
                      pendingConfigurationIteValuesListChanges={
                        originalConfigurationItemValues
                      }
                      configurationName={configurationName}
                      type="original"
                      dataType={dataType}
                      validationRules={validationRules}
                    />
                  ) : null}
                  {!isPartOfSiteDuplicationOrCreation && (
                    <Divider
                      sx={{
                        margin: "5px 0px",
                        opacity: 0.8
                      }}
                    />
                  )}
                  <PendingConfigurationItemValuesChange
                    pendingConfigurationIteValuesListChanges={
                      currentConfigurationItemValues
                    }
                    configurationName={configurationName}
                    type="new"
                    dataType={dataType}
                    validationRules={validationRules}
                  />
                </MainPendingChangesListContainer>
                <Divider
                  sx={{
                    margin: "10px 0px"
                  }}
                />
              </MainPendingChangesListContainer>
            );
          }
        )
      )}
    </MainContainer>
  );
}

export default PendingChangesList;
